import React, { useEffect, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth } from "../features/auth/authSlice";
import {
  searchProductsByBarcodePartial,
  fetchStores,
  createTransaction,
  checkCurrentShift,
  openShift,
  closeShift,
  selectSearchResults,
  selectSearchStatus,
  selectSearchError,
  selectSelectedProducts,
  selectStores,
  selectStoresStatus,
  selectStoresError,
  selectSelectedStore,
  selectTransactionStatus,
  selectTransactionError,
  selectLastReceiptNumber,
  selectCurrentShift,
  selectShiftStatus,
  selectShiftError,
  generateReceiptNumber,
  addProductToCart,
  incrementProductQuantity,
  decrementProductQuantity,
  removeProductFromCart,
  updateProductQuantity,
  updateProductDiscount,
  setSelectedStore,
  clearSelectedStore,
  clearShift,
  clearCart,
  clearSearchResults,
  incrementReceiptNumber,
} from "../features/onlineSeller/onlineSellerSlice";

export default function OnlineSellerPage() {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const [searchQuery, setSearchQuery] = useState("");
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [openShiftModalOpen, setOpenShiftModalOpen] = useState(false);
  const [tempSelectedStoreForShift, setTempSelectedStoreForShift] = useState(null);
  const [closeShiftModalOpen, setCloseShiftModalOpen] = useState(false);

  const searchResults = useSelector(selectSearchResults);
  const searchStatus = useSelector(selectSearchStatus);
  const searchError = useSelector(selectSearchError);
  const selectedProducts = useSelector(selectSelectedProducts);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);
  const storesError = useSelector(selectStoresError);
  const selectedStore = useSelector(selectSelectedStore);
  const transactionStatus = useSelector(selectTransactionStatus);
  const transactionError = useSelector(selectTransactionError);
  const lastReceiptNumber = useSelector(selectLastReceiptNumber);
  const currentShift = useSelector(selectCurrentShift);
  const shiftStatus = useSelector(selectShiftStatus);
  const shiftError = useSelector(selectShiftError);

  useEffect(() => {
    if (shiftStatus === "idle") {
      dispatch(checkCurrentShift());
    }
  }, [dispatch, shiftStatus]);

  useEffect(() => {
    return () => {
      dispatch(clearSearchResults());
    };
  }, [dispatch]);

  useEffect(() => {
    if (currentShift && !selectedStore) {
      if (storesStatus === "idle") {
        dispatch(fetchStores());
      } else if (storesStatus === "succeeded" && stores.length > 0) {
        const storeId = typeof currentShift.store === "object" ? currentShift.store.id : currentShift.store;
        if (storeId) {
          const store = stores.find((s) => s.id === storeId);
          if (store) {
            dispatch(setSelectedStore(store));
          }
        }
      }
    }
  }, [currentShift, selectedStore, storesStatus, stores, dispatch]);
  const sellerName = (() => {
    const first = auth.user?.first_name?.trim() || "";
    const last = auth.user?.last_name?.trim() || "";
    const full = `${first} ${last}`.trim();
    return full || auth.user?.username || "Продавец";
  })();

  const handleOpenShiftClick = () => {
    setOpenShiftModalOpen(true);
    if (storesStatus === "idle" || stores.length === 0) {
      dispatch(fetchStores());
    }
  };

  const handleSelectStoreForShift = (storeId) => {
    const store = stores.find((s) => s.id === storeId);
    if (store) {
      setTempSelectedStoreForShift(store);
    }
  };

  const handleConfirmOpenShift = async () => {
    if (!tempSelectedStoreForShift) return;

    const storeId = tempSelectedStoreForShift.id;
    
    try {
      dispatch(setSelectedStore(tempSelectedStoreForShift));
      const checkResult = await dispatch(checkCurrentShift()).unwrap();
      
      const hasValidShift = checkResult && checkResult.store !== null && checkResult.store !== undefined;
      
      let currentStoreId = null;
      if (hasValidShift) {
        if (typeof checkResult.store === "object" && checkResult.store !== null) {
          currentStoreId = checkResult.store.id;
        } else {
          currentStoreId = checkResult.store;
        }
      }
      
      if (hasValidShift && currentStoreId === storeId) {
        setOpenShiftModalOpen(false);
        setTempSelectedStoreForShift(null);
      } else {
        await dispatch(openShift({ storeId, openingAmount: "0", notes: "" })).unwrap();
        await dispatch(checkCurrentShift()).unwrap();
        setOpenShiftModalOpen(false);
        setTempSelectedStoreForShift(null);
      }
      } catch (err) {
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    
    if (!selectedStore) return;
    
    const storeId = typeof selectedStore === "object" ? selectedStore.id : selectedStore;
    await dispatch(searchProductsByBarcodePartial({ barcodePartial: query, storeId }));
  };

  useEffect(() => {
    if (searchStatus === "succeeded" && searchResults.length === 1) {
      dispatch(addProductToCart(searchResults[0]));
      setSearchQuery("");
    }
  }, [searchStatus, searchResults, dispatch]);

  const handleIncrement = (productId) => {
    dispatch(incrementProductQuantity(productId));
  };

  const handleDecrement = (productId) => {
    dispatch(decrementProductQuantity(productId));
  };

  const handleRemove = (productId) => {
    dispatch(removeProductFromCart(productId));
  };

  const handleQuantityChange = (productId, value) => {
    dispatch(updateProductQuantity({ productId, quantity: value }));
  };

  const handleDiscountChange = (productId, value) => {
    const trimmed = String(value || "").trim();
    if (trimmed === "" || trimmed === "0") {
      dispatch(updateProductDiscount({ productId, discount: "" }));
    } else {
      const numValue = trimmed.replace(/^0+/, "") || "0";
      dispatch(updateProductDiscount({ productId, discount: numValue }));
    }
  };

  const handleAddProduct = (product) => {
    dispatch(addProductToCart(product));
    setSearchQuery("");
  };

  const handleOpenStoreModal = () => {
    setStoreModalOpen(true);
    if (storesStatus === "idle") {
      dispatch(fetchStores());
    }
  };

  const handleOpenCloseShiftModal = () => {
    if (!selectedStore || !currentShift) return;
    const storeId = typeof selectedStore === "object" ? selectedStore.id : selectedStore;
    if (!storeId || isNaN(Number(storeId)) || Number(storeId) <= 0) {
      alert("Ошибка: невалидный ID магазина.");
      return;
    }
    setCloseShiftModalOpen(true);
  };

  const handleConfirmCloseShift = async () => {
    if (!selectedStore || !currentShift) return;

    const storeId = typeof selectedStore === "object" ? selectedStore.id : selectedStore;
    if (!storeId || isNaN(Number(storeId)) || Number(storeId) <= 0) return;

    try {
      await dispatch(closeShift({ storeId, closingAmount: "0", notes: "" })).unwrap();
      dispatch(clearSelectedStore());
      dispatch(clearShift());
      setCloseShiftModalOpen(false);
    } catch (err) {
      // Ошибка отображается через shiftError в модальном окне
    }
  };

  const handleSelectStore = (store) => {
    dispatch(setSelectedStore(store));
    setStoreModalOpen(false);
  };

  const handlePay = () => {
    if (!selectedStore || selectedProducts.length === 0) return;
    setPaymentModalOpen(true);
  };

  const handleConfirmPayment = async () => {
    if (!selectedStore || selectedProducts.length === 0) return;

    const storeId = typeof selectedStore === "object" ? selectedStore.id : selectedStore;
    if (!storeId || isNaN(Number(storeId)) || Number(storeId) <= 0) return;

    try {
      const nextReceiptNumber = lastReceiptNumber + 1;
      const receipt = generateReceiptNumber(nextReceiptNumber);

      const discountTotal = selectedProducts.reduce((sum, item) => {
        const discount = Number(item.discount) || 0;
        return sum + discount;
      }, 0);

      const products = selectedProducts.map((item) => {
        const costPrice = Number(item.product.cost_price) || 0;
        const discount = Number(item.discount) || 0;
        const quantity = Number(item.quantity) || 1;
        const productId = Number(item.product.id);

        if (!productId || isNaN(productId) || productId <= 0) {
          throw new Error(`Невалидный ID товара: ${item.product.id}`);
        }

        return {
          product_id: productId,
          quantity: String(quantity),
          discount: String(discount.toFixed(2)),
          sales_price: String(costPrice.toFixed(2)),
        };
      });

      await dispatch(
        createTransaction({
          storeId: Number(storeId),
          receipt: receipt,
          discountTotal: String(discountTotal.toFixed(2)),
          paymentType: "online",
          products: products,
        })
      ).unwrap();
      
      dispatch(clearCart());
      dispatch(clearSearchResults());
      dispatch(incrementReceiptNumber());
      
      setPaymentModalOpen(false);
    } catch (err) {
    }
  };

  const totalAmount = selectedProducts.reduce((sum, item) => {
    const costPrice = Number(item.product.cost_price) || 0;
    const quantity = Number(item.quantity) || 1;
    const discount = Number(item.discount) || 0;
    const subtotal = costPrice * quantity;
    return sum + Math.max(0, subtotal - discount);
  }, 0);

  const totalDiscount = selectedProducts.reduce((sum, item) => {
    return sum + (Number(item.discount) || 0);
  }, 0);

  const isShiftValid = currentShift && selectedStore;
  const isInitialCheck = (shiftStatus === "idle" || shiftStatus === "loading") && !selectedStore && !currentShift;
  const shouldShowShiftScreen = isInitialCheck || (shiftStatus === "succeeded" && !isShiftValid);
  
  if (shouldShowShiftScreen) {
    return (
      <div style={styles.page}>
        <div style={styles.shiftScreen}>
          <div style={styles.shiftCard}>
            <div style={styles.shiftTitle}>Открытие смены</div>
            <div style={styles.shiftText}>
              {currentShift && !selectedStore
                ? "Смена открыта, но магазин не выбран. Пожалуйста, выберите магазин для продолжения работы."
                : "Для начала работы необходимо открыть смену. Выберите магазин и откройте смену."}
            </div>
            {shiftStatus === "loading" && <div style={styles.notice}>
              {currentShift ? "Открытие смены..." : "Проверка смены..."}
            </div>}
            {shiftError && <div style={styles.error}>{shiftError}</div>}
            <button 
              type="button" 
              onClick={handleOpenShiftClick} 
              style={styles.openShiftButton}
              disabled={shiftStatus === "loading"}
            >
              {currentShift && !selectedStore ? "Выбрать магазин" : "Открыть смену"}
            </button>
          </div>
        </div>

        {openShiftModalOpen && (
          <div style={styles.modalOverlay} onMouseDown={() => {
            setOpenShiftModalOpen(false);
            setTempSelectedStoreForShift(null);
          }}>
            <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div style={styles.modalTitle}>Выбрать магазин для открытия смены</div>
                <button type="button" onClick={() => {
                  setOpenShiftModalOpen(false);
                  setTempSelectedStoreForShift(null);
                }} style={styles.modalClose}>
                  ×
                </button>
              </div>

              <div style={styles.modalBody}>
                {storesStatus === "loading" && <Loading text="Загрузка магазинов…" />}
                {storesStatus === "failed" && storesError && (
                  <div style={styles.error}>{storesError}</div>
                )}
                {shiftError && <div style={styles.error}>{shiftError}</div>}
                
                {tempSelectedStoreForShift ? (
                  // Показываем выбранный магазин и кнопку "Открыть смену"
                  <div style={styles.selectedStoreSection}>
                    <div style={styles.selectedStoreCard}>
                      <div style={styles.selectedStoreLabel}>Выбранный магазин:</div>
                      <div style={styles.selectedStoreName}>{tempSelectedStoreForShift.name || `Магазин #${tempSelectedStoreForShift.id}`}</div>
                      {tempSelectedStoreForShift.address && (
                        <div style={styles.selectedStoreAddress}>{tempSelectedStoreForShift.address}</div>
                      )}
                      <div style={styles.selectedStoreId}>ID: {tempSelectedStoreForShift.id}</div>
                    </div>
                    <button
                      type="button"
                      onClick={handleConfirmOpenShift}
                      style={styles.confirmOpenShiftButton}
                      disabled={shiftStatus === "loading"}
                    >
                      {shiftStatus === "loading" ? "Открытие смены..." : "Открыть смену"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setTempSelectedStoreForShift(null)}
                      style={styles.changeStoreButton}
                      disabled={shiftStatus === "loading"}
                    >
                      Выбрать другой магазин
                    </button>
                  </div>
                ) : (
                  storesStatus === "succeeded" && (
                    <>
                      {stores.length === 0 ? (
                        <div style={styles.empty}>Магазины не найдены</div>
                      ) : (
                        <div style={styles.storesList}>
                          {stores.map((store) => {
                            const isCurrentShiftStore =
                              currentShift &&
                              (typeof currentShift.store === "object"
                                ? currentShift.store.id === store.id
                                : currentShift.store === store.id);

                            return (
                              <button
                                key={store.id}
                                type="button"
                                onClick={() => handleSelectStoreForShift(store.id)}
                                style={{
                                  ...styles.storeItem,
                                  ...(isCurrentShiftStore ? styles.storeItemSelected : null),
                                }}
                                disabled={shiftStatus === "loading"}
                              >
                                <div style={styles.storeItemName}>{store.name || `Магазин #${store.id}`}</div>
                                {store.address && (
                                  <div style={styles.storeItemAddress}>{store.address}</div>
                                )}
                                <div style={styles.storeItemId}>ID: {store.id}</div>
                                {isCurrentShiftStore && (
                                  <div style={styles.storeItemBadge}>Текущая смена</div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {selectedStore && shiftStatus === "loading" && !currentShift && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingMessage}>Открытие смены...</div>
        </div>
      )}
      <div style={styles.header}>
        <div style={styles.sellerInfo}>
          <div style={styles.sellerLabel}>Продавец</div>
          <div style={styles.sellerName}>{sellerName}</div>
          {currentShift && (
            <div style={styles.shiftInfo}>
              <div style={styles.shiftLabel}>Смена открыта</div>
              <div style={styles.shiftStore}>
                {currentShift.store_name || `Магазин #${currentShift.store}` || "—"}
              </div>
              <button
                type="button"
                onClick={handleOpenCloseShiftModal}
                style={styles.closeShiftButton}
                disabled={shiftStatus === "loading"}
              >
                Закрыть смену
              </button>
            </div>
          )}
        </div>

        <div style={styles.headerActions}>
          <button type="button" onClick={handleOpenStoreModal} style={styles.storeButton}>
            {selectedStore ? `${selectedStore.name || `Магазин #${selectedStore.id}`}` : "Выбрать магазин"}
          </button>

          <form onSubmit={handleSearch} style={styles.searchForm}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск товара по названию, штрихкоду..."
              style={styles.searchInput}
              autoFocus
            />
            <button type="submit" style={styles.searchButton}>
              Поиск
            </button>
          </form>
        </div>
      </div>

      <div style={styles.content}>
        {/* Статус поиска */}
        {searchStatus === "loading" && <div style={styles.notice}>Поиск товаров...</div>}
        {searchStatus === "failed" && searchError && (
          <div style={styles.error}>{searchError}</div>
        )}
        {searchStatus === "succeeded" && searchResults.length === 1 && (
          <div style={styles.notice}>
            Найден 1 товар. Товар автоматически добавлен в список.
          </div>
        )}

        {/* Результаты поиска - таблица (только если найдено несколько товаров) */}
        {searchStatus === "succeeded" && searchResults.length > 1 && (
          <div style={styles.searchSection}>
            <div style={styles.sectionTitle}>Результаты поиска ({searchResults.length})</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Название</th>
                    <th style={styles.th}>Штрихкод</th>
                    <th style={styles.th}>Цена</th>
                    <th style={styles.th}>Действие</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((product) => (
                    <tr
                      key={product.id}
                      style={styles.tr}
                      onClick={() => handleAddProduct(product)}
                      title="Нажмите, чтобы добавить в список"
                    >
                      <td style={styles.tdMono}>{product.id || "—"}</td>
                      <td style={styles.td}>{product.name || "—"}</td>
                      <td style={styles.tdMono}>{product.barcode || "—"}</td>
                      <td style={styles.td}>
                        {(() => {
                          const costPrice = Number(product.cost_price);
                          return Number.isFinite(costPrice) ? costPrice.toFixed(2) : "—";
                        })()}
                      </td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddProduct(product);
                          }}
                          style={styles.btnAdd}
                        >
                          Добавить
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Список выбранных товаров */}
        {selectedProducts.length > 0 && (
          <div style={styles.cartSection}>
            <div style={styles.cartHeader}>
              <div style={styles.sectionTitle}>Список товаров ({selectedProducts.length})</div>
              <div style={styles.cartSummary}>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Скидка:</span>
                  <span style={styles.summaryValue}>{totalDiscount.toFixed(2)} сом</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Итого:</span>
                  <span style={styles.summaryTotal}>{totalAmount.toFixed(2)} сом</span>
                </div>
                <button
                  type="button"
                  onClick={handlePay}
                  style={styles.payButton}
                  disabled={!selectedStore || transactionStatus === "loading"}
                >
                  {transactionStatus === "loading" ? "Оплата..." : "Оплатить"}
                </button>
              </div>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Название</th>
                    <th style={styles.th}>Штрихкод</th>
                    <th style={styles.th}>Цена</th>
                    <th style={styles.th}>Количество</th>
                    <th style={styles.th}>Скидка</th>
                    <th style={styles.th}>Сумма</th>
                    <th style={styles.th}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProducts.map((item) => {
                    const costPrice = Number(item.product.cost_price) || 0;
                    const quantity = Number(item.quantity) || 1;
                    const discount = Number(item.discount) || 0;
                    const subtotal = costPrice * quantity;
                    const total = Math.max(0, subtotal - discount);
                    return (
                      <tr key={item.product.id} style={styles.tr}>
                        <td style={styles.tdMono}>{item.product.id || "—"}</td>
                        <td style={styles.td}>{item.product.name || "—"}</td>
                        <td style={styles.tdMono}>{item.product.barcode || "—"}</td>
                        <td style={styles.td}>
                          {Number.isFinite(costPrice) ? costPrice.toFixed(2) : "—"}
                        </td>
                        <td style={styles.td}>
                          <div style={styles.quantityControls}>
                            <button
                              type="button"
                              onClick={() => handleDecrement(item.product.id)}
                              style={styles.qtyBtn}
                              title="Уменьшить"
                            >
                              −
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleQuantityChange(item.product.id, e.target.value)}
                              style={styles.qtyInput}
                            />
                            <button
                              type="button"
                              onClick={() => handleIncrement(item.product.id)}
                              style={styles.qtyBtn}
                              title="Увеличить"
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={discount === 0 ? "" : discount}
                            onChange={(e) => handleDiscountChange(item.product.id, e.target.value)}
                            placeholder="0"
                            style={styles.discountInput}
                            title="Скидка в сомах (целое число)"
                          />
                        </td>
                        <td style={styles.td}>{total.toFixed(2)}</td>
                        <td style={styles.td}>
                          <button
                            type="button"
                            onClick={() => handleRemove(item.product.id)}
                            style={styles.btnRemove}
                            title="Удалить товар"
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {searchStatus === "succeeded" && searchResults.length === 0 && (
          <div style={styles.empty}>Товары не найдены</div>
        )}
        {searchStatus === "idle" && selectedProducts.length === 0 && (
          <div style={styles.empty}>Выполните поиск товара. Найденные товары автоматически добавятся в список.</div>
        )}
      </div>

      {/* Модальное окно выбора магазина */}
      {storeModalOpen && (
        <div style={styles.modalOverlay} onMouseDown={() => setStoreModalOpen(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Выбрать магазин</div>
              <button type="button" onClick={() => setStoreModalOpen(false)} style={styles.modalClose}>
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              {storesStatus === "loading" && <Loading text="Загрузка магазинов…" />}
              {storesStatus === "failed" && storesError && (
                <div style={styles.error}>{storesError}</div>
              )}
              {storesStatus === "succeeded" && (
                <>
                  {stores.length === 0 ? (
                    <div style={styles.empty}>Магазины не найдены</div>
                  ) : (
                    <div style={styles.storesList}>
                      {stores.map((store) => (
                        <button
                          key={store.id}
                          type="button"
                          onClick={() => handleSelectStore(store)}
                          style={{
                            ...styles.storeItem,
                            ...(selectedStore?.id === store.id ? styles.storeItemSelected : null),
                          }}
                        >
                          <div style={styles.storeItemName}>{store.name || `Магазин #${store.id}`}</div>
                          {store.address && (
                            <div style={styles.storeItemAddress}>{store.address}</div>
                          )}
                          <div style={styles.storeItemId}>ID: {store.id}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно оплаты */}
      {paymentModalOpen && (
        <div style={styles.modalOverlay} onMouseDown={() => setPaymentModalOpen(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Оплата</div>
              <button type="button" onClick={() => setPaymentModalOpen(false)} style={styles.modalClose}>
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              {transactionError && (
                <div style={styles.error}>
                  <div style={styles.errorTitle}>Ошибка оплаты</div>
                  <div>
                    {typeof transactionError === "string"
                      ? transactionError
                      : transactionError?.detail || transactionError?.message || "Произошла ошибка при обработке оплаты"}
                  </div>
                </div>
              )}

              <div style={styles.paymentInfo}>
                <div style={styles.paymentInfoRow}>
                  <span style={styles.paymentLabel}>Магазин:</span>
                  <span style={styles.paymentValue}>
                    {selectedStore?.name || `Магазин #${selectedStore?.id}` || "—"}
                  </span>
                </div>
                <div style={styles.paymentInfoRow}>
                  <span style={styles.paymentLabel}>Товаров:</span>
                  <span style={styles.paymentValue}>{selectedProducts.length}</span>
                </div>
                <div style={styles.paymentInfoRow}>
                  <span style={styles.paymentLabel}>Скидка:</span>
                  <span style={styles.paymentValue}>{totalDiscount.toFixed(2)} сом</span>
                </div>
                <div style={styles.paymentInfoRow}>
                  <span style={styles.paymentLabel}>Итого:</span>
                  <span style={styles.paymentTotal}>{totalAmount.toFixed(2)} сом</span>
                </div>
                <div style={styles.paymentInfoRow}>
                  <span style={styles.paymentLabel}>Тип оплаты:</span>
                  <span style={styles.paymentValue}>Онлайн</span>
                </div>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={handleConfirmPayment}
                  style={styles.confirmButton}
                  disabled={transactionStatus === "loading"}
                >
                  {transactionStatus === "loading" ? "Оплата..." : "Подтвердить оплату"}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentModalOpen(false)}
                  style={styles.cancelButton}
                  disabled={transactionStatus === "loading"}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {closeShiftModalOpen && selectedStore && currentShift && (
        <div style={styles.modalOverlay} onMouseDown={() => setCloseShiftModalOpen(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Закрытие смены</div>
              <button type="button" onClick={() => setCloseShiftModalOpen(false)} style={styles.modalClose}>
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              {shiftError && (
                <div style={styles.error}>
                  {typeof shiftError === "string" ? shiftError : shiftError?.detail || shiftError?.message || "Ошибка закрытия смены"}
                </div>
              )}
              <div style={styles.paymentInfo}>
                <div style={styles.paymentInfoRow}>
                  <span style={styles.paymentLabel}>Магазин:</span>
                  <span style={styles.paymentValue}>
                    {selectedStore?.name || `Магазин #${selectedStore?.id}` || "—"}
                  </span>
                </div>
                {selectedStore?.address && (
                  <div style={styles.paymentInfoRow}>
                    <span style={styles.paymentLabel}>Адрес:</span>
                    <span style={styles.paymentValue}>{selectedStore.address}</span>
                  </div>
                )}
                <div style={styles.paymentInfoRow}>
                  <span style={styles.paymentLabel}>Смена:</span>
                  <span style={styles.paymentValue}>
                    {currentShift?.opened_at
                      ? new Date(currentShift.opened_at).toLocaleString("ru-RU")
                      : currentShift?.id ? `#${currentShift.id}` : "—"}
                  </span>
                </div>
                <div style={styles.paymentInfoRow}>
                  <span style={styles.paymentLabel}>Кассир:</span>
                  <span style={styles.paymentValue}>{sellerName}</span>
                </div>
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={handleConfirmCloseShift}
                  style={styles.closeShiftConfirmButton}
                  disabled={shiftStatus === "loading"}
                >
                  {shiftStatus === "loading" ? "Закрытие..." : "Закрыть"}
                </button>
                <button
                  type="button"
                  onClick={() => setCloseShiftModalOpen(false)}
                  style={styles.cancelButton}
                  disabled={shiftStatus === "loading"}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--c-bg)",
    color: "var(--c-text)",
  },
  header: {
    padding: "18px 20px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  storeButton: {
    padding: "12px 20px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
    whiteSpace: "nowrap",
  },
  sellerInfo: {
    display: "grid",
    gap: 4,
  },
  sellerLabel: {
    fontSize: 14,
    fontWeight: 900,
    color: "var(--c-text-sub)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  sellerName: {
    fontSize: 20,
    fontWeight: 900,
    color: "var(--c-text)",
  },
  searchForm: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    maxWidth: 600,
  },
  searchInput: {
    flex: 1,
    minWidth: 200,
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
  },
  searchButton: {
    padding: "12px 20px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
    whiteSpace: "nowrap",
  },
  content: {
    flex: 1,
    padding: "12px 20px 20px 20px",
    overflow: "auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  notice: {
    padding: "8px 18px",
    fontSize: 15,
    opacity: 0.85,
    textAlign: "center",
    marginBottom: 0,
  },
  error: {
    padding: "12px 18px",
    fontSize: 15,
    background: "var(--c-danger-dim)",
    borderTop: "1px solid var(--c-danger)",
    borderBottom: "1px solid var(--c-danger)",
    borderRadius: 12,
    color: "var(--c-danger)",
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: 900,
    marginBottom: 6,
    color: "var(--c-danger)",
  },
  empty: {
    padding: 40,
    textAlign: "center",
    fontSize: 16,
    opacity: 0.75,
  },
  searchSection: {
    display: "grid",
    gap: 12,
  },
  cartSection: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  cartHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    flexWrap: "wrap",
  },
  cartSummary: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-end",
  },
  paymentTypeInline: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 16,
  },
  summaryLabel: {
    opacity: 0.85,
    fontWeight: 700,
  },
  summaryValue: {
    fontWeight: 900,
    color: "var(--c-text)",
  },
  summaryTotal: {
    fontWeight: 900,
    fontSize: 20,
    color: "var(--c-text)",
  },
  paymentTypeSelectInline: {
    width: 220,
    maxWidth: "70vw",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
  },
  wholesaleHint: {
    maxWidth: 360,
    fontSize: 14,
    fontWeight: 800,
    opacity: 0.8,
    textAlign: "right",
  },
  payButton: {
    padding: "12px 24px",
    borderRadius: 99,
    border: "1px solid rgba(34, 197, 94, 0.35)",
    background: "rgba(34, 197, 94, 0.18)",
    color: "var(--c-success)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 17,
    whiteSpace: "nowrap",
    marginTop: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 900,
    color: "var(--c-text)",
    padding: "0",
    margin: 0,
  },
  tableWrap: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  th: {
    textAlign: "left",
    padding: "12px 14px",
    fontSize: 14,
    fontWeight: 900,
    color: "var(--c-text-sub)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    whiteSpace: "nowrap",
  },
  tr: {
    cursor: "pointer",
    transition: "background 120ms ease",
  },
  td: {
    padding: "12px 14px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
  },
  tdMono: {
    padding: "12px 14px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  btnAdd: {
    padding: "6px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 14,
    whiteSpace: "nowrap",
  },
  quantityControls: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  qtyBtn: {
    width: 32,
    height: 32,
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  qtyInput: {
    width: 60,
    padding: "6px 8px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    textAlign: "center",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  discountInput: {
    width: 100,
    padding: "6px 8px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    textAlign: "center",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  btnRemove: {
    padding: "6px 12px",
    borderRadius: 99,
    border: "1px solid rgba(251, 113, 133, 0.35)",
    background: "rgba(251, 113, 133, 0.14)",
    color: "var(--c-danger)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 14,
    whiteSpace: "nowrap",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 50,
  },
  modal: {
    width: "min(600px, 90vw)",
    maxHeight: "80vh",
    background: "var(--c-bg)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  modalHeader: {
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid var(--c-border)",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 900,
    color: "var(--c-text)",
  },
  modalClose: {
    width: 34,
    height: 34,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 20,
    lineHeight: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: {
    padding: 16,
    overflow: "auto",
    flex: 1,
    minHeight: 0,
  },
  storesList: {
    display: "grid",
    gap: 10,
  },
  storeItem: {
    width: "100%",
    textAlign: "left",
    padding: "14px 16px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    cursor: "pointer",
    transition: "background 120ms ease, border-color 120ms ease",
    display: "grid",
    gap: 6,
  },
  storeItemSelected: {
    border: "1px solid rgba(245,197,24,0.45)",
    background: "rgba(245,197,24,0.08)",
  },
  storeItemName: {
    fontSize: 17,
    fontWeight: 900,
    color: "var(--c-text)",
  },
  storeItemAddress: {
    fontSize: 15,
    opacity: 0.85,
    lineHeight: 1.4,
  },
  storeItemId: {
    fontSize: 14,
    opacity: 0.65,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  storeItemBadge: {
    marginTop: 6,
    padding: "4px 8px",
    borderRadius: 99,
    background: "rgba(34, 197, 94, 0.18)",
    border: "1px solid rgba(34, 197, 94, 0.35)",
    color: "var(--c-success)",
    fontSize: 13,
    fontWeight: 900,
  },
  shiftScreen: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  shiftCard: {
    maxWidth: 500,
    width: "100%",
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    padding: 32,
    textAlign: "center",
    display: "grid",
    gap: 20,
  },
  shiftTitle: {
    fontSize: 25,
    fontWeight: 900,
    color: "var(--c-text)",
    marginBottom: 8,
  },
  shiftText: {
    fontSize: 16,
    opacity: 0.85,
    lineHeight: 1.6,
    color: "var(--c-text)",
  },
  openShiftButton: {
    padding: "14px 28px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-accent)",
    color: "var(--c-accent-on)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 18,
    marginTop: 8,
  },
  shiftInfo: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid var(--c-border)",
    display: "grid",
    gap: 4,
  },
  shiftLabel: {
    fontSize: 13,
    fontWeight: 900,
    color: "var(--c-text-sub)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  shiftStore: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--c-success)",
  },
  closeShiftButton: {
    marginTop: 8,
    padding: "8px 16px",
    borderRadius: 99,
    border: "1px solid rgba(239, 68, 68, 0.35)",
    background: "rgba(239, 68, 68, 0.18)",
    color: "var(--c-danger)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  loadingOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
  },
  loadingMessage: {
    padding: "20px 32px",
    background: "var(--c-bg)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    color: "var(--c-text)",
    fontSize: 18,
    fontWeight: 900,
  },
  paymentInfo: {
    display: "grid",
    gap: 12,
    padding: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 12,
    marginBottom: 16,
  },
  paymentInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 16,
  },
  paymentLabel: {
    opacity: 0.85,
    fontWeight: 700,
  },
  paymentValue: {
    fontWeight: 900,
    color: "var(--c-text)",
  },
  paymentTotal: {
    fontWeight: 900,
    fontSize: 20,
    color: "var(--c-text)",
  },
  paymentTypeSection: {
    display: "grid",
    gap: 10,
    marginBottom: 16,
  },
  paymentTypeLabel: {
    fontSize: 16,
    fontWeight: 900,
    color: "var(--c-text)",
  },
  paymentTypeSelect: {
    width: "100%",
    maxWidth: 360,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
  },
  modalActions: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 16,
  },
  confirmButton: {
    padding: "12px 24px",
    borderRadius: 99,
    border: "1px solid rgba(34, 197, 94, 0.35)",
    background: "rgba(34, 197, 94, 0.18)",
    color: "var(--c-success)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
  },
  cancelButton: {
    padding: "12px 24px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
  },
  closeShiftConfirmButton: {
    padding: "12px 24px",
    borderRadius: 99,
    border: "1px solid rgba(239, 68, 68, 0.35)",
    background: "rgba(239, 68, 68, 0.18)",
    color: "var(--c-danger)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
  },
  selectedStoreSection: {
    display: "grid",
    gap: 16,
  },
  selectedStoreCard: {
    padding: 20,
    border: "1px solid var(--c-border)",
    background: "rgba(245,197,24,0.08)",
    borderRadius: 16,
    display: "grid",
    gap: 8,
  },
  selectedStoreLabel: {
    fontSize: 14,
    fontWeight: 900,
    color: "var(--c-text-sub)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  selectedStoreName: {
    fontSize: 20,
    fontWeight: 900,
    color: "var(--c-accent)",
  },
  selectedStoreAddress: {
    fontSize: 16,
    opacity: 0.85,
    lineHeight: 1.4,
  },
  selectedStoreId: {
    fontSize: 15,
    opacity: 0.75,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  confirmOpenShiftButton: {
    padding: "14px 24px",
    borderRadius: 99,
    border: "1px solid rgba(34, 197, 94, 0.35)",
    background: "rgba(34, 197, 94, 0.18)",
    color: "var(--c-success)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 18,
    width: "100%",
  },
  changeStoreButton: {
    padding: "10px 20px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 16,
    width: "100%",
  },
};
